import React from 'react';
import { Linkedin, Twitter, Github, Mail, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DisionLogo from './DisionLogo';

const footerLinks = {
  Development: [
    { label: 'Software Product Development', href: '/development/software-product-development' },
    { label: 'Web Application Development', href: '/development/web-application-development' },
    { label: 'DevNetOps Services', href: '/development/dev-net-ops-services' },
    { label: 'IT Consulting Services', href: '/development/it-consulting-services' },
  ],
  Services: [
    { label: 'Network Security', href: '/services/network-security' },
    { label: 'Cybersecurity Assessment', href: '/services/cybersecurity' },
    { label: 'Cloud Infrastructure', href: '/services/cloud-infrastructure' },
    { label: 'UI/UX Design', href: '/services/ui-ux-design' },
    { label: 'Data Analytics', href: '/services/data-analytics' },
  ],
  Company: [
    { label: 'About Us', href: '/about-us' },
    { label: 'Projects', href: '/' },
    { label: 'News', href: '/news' },
    { label: 'Jobs', href: '/jobs' },
    { label: 'Contact', href: '/about-us' },
  ],
};

const socialLinks = [
  { icon: Linkedin, href: '#', label: 'LinkedIn' },
  { icon: Twitter, href: '#', label: 'Twitter' },
  { icon: Github, href: '#', label: 'GitHub' },
];

export default function Footer() {
  const navigate = useNavigate();
  return (
    <footer style={{ backgroundColor: '#0F172A', color: '#94A3B8' }}>
      <style>{`
        @media (max-width: 768px) {
          .footer-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
        }
      `}</style>
      {/* Main footer */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '72px 24px 48px' }}>
        <div className="footer-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '48px' }}>
          {/* Brand column */}
          <div>
            <button onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
              <DisionLogo height={26} dark={true} />
            </button>
            <p style={{ fontSize: '15px', lineHeight: 1.75, color: '#94A3B8', marginBottom: '24px', maxWidth: '300px' }}>
              DISION Tech is providing innovative and effective software development products and cyber security solutions as a trusted partner for businesses of all sizes and industries.
            </p>

            {/* Contact info */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Mail size={16} color="#0099CC" />
                <a href="mailto:info@dision.tech" style={{ color: '#94A3B8', textDecoration: 'none', fontSize: '14px', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'white'}
                  onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
                  info@dision.tech
                </a>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <MapPin size={16} color="#0099CC" />
                <span style={{ color: '#94A3B8', fontSize: '14px' }}>Ho Chi Minh, Vietnam</span>
              </div>
            </div>

            {/* Social links */}
            <div style={{ display: 'flex', gap: '12px' }}>
              {socialLinks.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  style={{
                    width: '40px', height: '40px',
                    borderRadius: '10px',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    textDecoration: 'none',
                    transition: 'background-color 0.2s ease, border-color 0.2s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(0,153,204,0.15)'; e.currentTarget.style.borderColor = '#0099CC'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}
                >
                  <Icon size={18} color="#94A3B8" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 style={{ fontSize: '14px', fontWeight: '700', color: 'white', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px' }}>
                {category}
              </h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {links.map(link => (
                  <li key={link.label}>
                    <button
                      onClick={() => navigate(link.href)}
                      style={{
                        color: '#94A3B8',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '15px',
                        transition: 'color 0.2s ease',
                        lineHeight: 1.5,
                        padding: 0,
                        textAlign: 'left',
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = 'white'}
                      onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <span style={{ fontSize: '14px', color: '#64748B' }}>
            © {new Date().getFullYear()} DISION Tech LLC. All rights reserved.
          </span>
          <div style={{ display: 'flex', gap: '24px' }}>
            {['Privacy Policy', 'Terms of Service'].map(label => (
              <a
                key={label}
                href="#"
                style={{ fontSize: '14px', color: '#64748B', textDecoration: 'none', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#94A3B8'}
                onMouseLeave={e => e.currentTarget.style.color = '#64748B'}
              >
                {label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
